/**
 * Utility & helper functions shared across Travel Order components.
 * Add new helpers here as they're extracted, keeping each one pure
 * (no component state, no side effects) so they stay easy to test
 * and reuse.
 */

/**
 * Formats a numeric amount as a currency string using Indian number
 * grouping, with a couple of extra spaces after the currency symbol
 * for visual breathing room (matches existing TravelOrderDetailView usage).
 */
export const formatAmount = (amount, currency) => {
    const formatter = new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: currency || "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return formatter
        .formatToParts(amount || 0)
        .map((part) =>
            part.type === "currency"
                ? `${part.value}\u00A0`
                : part.value
        )
        .join("");
};
/**
 * "Adult" or "Child" for a traveler on the order detail.
 *
 * Website bookings record the type the customer chose at checkout, which wins.
 * Otherwise the age (from date of birth) decides; with no age at all the
 * traveler is counted as an adult, the same default the website uses for
 * orders placed before it asked about children.
 */
export const getTravelerType = (traveler) => {
    if (traveler?.passenger_type === "Adult" || traveler?.passenger_type === "Child") {
        return traveler.passenger_type;
    }
    if (typeof traveler?.age === "number") return traveler.age >= 18 ? "Adult" : "Child";
    return "Adult";
};

/** Adult and child counts for an order's travelers. */
export const countTravelerTypes = (travelers) => {
    const list = Array.isArray(travelers) ? travelers : [];
    const children = list.filter((traveler) => getTravelerType(traveler) === "Child").length;
    return { adults: list.length - children, children };
};

/** "2 Adults, 1 Child" style summary; empty string for no travelers. */
export const formatTravelerMix = (travelers) => {
    const { adults, children } = countTravelerTypes(travelers);
    if (!adults && !children) return "";
    const parts = [`${adults} Adult${adults === 1 ? "" : "s"}`];
    if (children) parts.push(`${children} Child${children === 1 ? "" : "ren"}`);
    return parts.join(", ");
};
